import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/data/content";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-brand focus:ring-1 focus:ring-emerald-brand transition-colors";
const labelCls = "block text-sm font-semibold text-navy mb-1.5";

// Simple single-screen lead form used on Contact & Country pages.
export default function LeadForm({ formName = "General Inquiry", serviceKey = null, countryKey = null, title, testid = "lead-form" }) {
  const [data, setData] = useState({ full_name: "", email: "", phone: "", service_required: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: k === "consent" ? e.target.checked : e.target.value }));

  const valid = data.full_name && /.+@.+\..+/.test(data.email) && data.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Please complete the required fields and consent."); return; }
    setSubmitting(true);
    try {
      const res = await submitLead({ ...data, form_name: formName, service_key: serviceKey, country_key: countryKey });
      setResult(res);
      toast.success("Inquiry sent successfully.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div data-testid={`${testid}-confirmation`} className="text-center py-8">
        <CheckCircle2 className="text-emerald-brand mx-auto mb-3" size={40} />
        <h3 className="text-xl font-bold text-navy">Inquiry Received</h3>
        <p className="text-sm mt-2">Reference: <span className="font-bold text-navy">{result.lead_id}</span></p>
        <p className="text-slate-500 text-sm mt-2">Our team will be in touch. Contact us to know more.</p>
      </div>
    );
  }

  return (
    <form data-testid={testid} onSubmit={submit} className="space-y-4">
      {title && <h3 className="text-xl font-bold text-navy">{title}</h3>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Full Name *</label><input data-testid={`${testid}-name`} className={inputCls} value={data.full_name} onChange={set("full_name")} /></div>
        <div><label className={labelCls}>Email *</label><input data-testid={`${testid}-email`} className={inputCls} value={data.email} onChange={set("email")} /></div>
        <div><label className={labelCls}>Phone or WhatsApp</label><input className={inputCls} value={data.phone} onChange={set("phone")} /></div>
        <div>
          <label className={labelCls}>Service Required</label>
          <select className={inputCls + " bg-white"} value={data.service_required} onChange={set("service_required")}>
            <option value="">Select a service</option>
            {SERVICE_OPTIONS.map((s) => <option key={s.label} value={s.label}>{s.label}</option>)}
          </select>
        </div>
      </div>
      <div><label className={labelCls}>Message</label><textarea rows={4} className={inputCls} value={data.message} onChange={set("message")} placeholder="Tell us about your business and goals." /></div>
      <label className="flex gap-3 text-sm text-slate-600 cursor-pointer">
        <input data-testid={`${testid}-consent`} type="checkbox" checked={data.consent} onChange={set("consent")} className="mt-1 w-5 h-5 accent-emerald-700 shrink-0" />
        <span>I understand that submitting this form does not create a professional-client relationship or guarantee any registration, banking, work-permit, visa, residence or tax result. See our <Link to="/legal/privacy-policy" className="text-emerald-brand underline font-semibold">Privacy Policy</Link>.</span>
      </label>
      <button data-testid={`${testid}-submit`} disabled={submitting} className="btn-gold rounded-full px-7 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60">
        {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send Inquiry"}
      </button>
    </form>
  );
}
